<script>
	import thumb_eoQ3UL96zwE from '$lib/Components/Youtube/images/eoQ3UL96zwE.jpg?w=610';

	import Button from '$lib/Components/Layout/Button/Button.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';

	import img2 from './images/Comparison-Window-Replacement-vs.-Storm-02.jpg';
	import img3 from './images/Comparison-Window-Replacement-vs.-Storm-03A.png';
	import img4 from './images/Comparison-Window-Replacement-vs.-Storm-03B.png';
	import img5 from './images/Comparison-Window-Replacement-vs.-Storm-03C.png';
	import img6 from './images/Comparison-Window-Replacement-vs.-Storm-04.jpg';
	import img7 from './images/Comparison-Window-Replacement-vs.-Storm-05.png';
	import img8 from './images/Comparison-Window-Replacement-vs.-Storm-07.jpg';
	import img1 from './images/Comparison-Window-Replacement.jpg';
</script>

<Section backgroundColor="white">
	<p>
		If you’re considering storm windows vs replacement windows, you know there
		are many options. Both will increase the comfort and <a
			href="/resources/blog/energy-efficient-window-myths">efficiency</a
		>
		of your home or building. For replacement windows, there’s new construction and
		replication. For storm windows, there’s
		<a href="/resources/storm-windows-guide/traditional"
			>exterior and interior storm windows</a
		>.
	</p>
	<Image
		src={img1}
		alt="View of entire home: learn if you should replace your windows or add storm windows to your existing."
		class="max-w-96"
	/>
	<p>
		When looking at storm windows or window inserts vs full replacement, you
		want an upgrade from what you already have. You want to reduce noise and
		drafts, increase energy savings and ease of care. You’ll also want to think
		about how it will affect the appearance of your home and whether you want to
		be able to open your windows. We’ll cover all of that in this comparison.
	</p>
	<nav>
		<h2>Comparison Sections</h2>
		<ul class="flex list-none flex-col items-center text-center">
			<li><a href="#Construction">Construction</a></li>
			<li><a href="#Performance">Performance</a></li>
			<li><a href="#Installation">Installation</a></li>
			<li><a href="#Cost">Cost</a></li>
		</ul>
	</nav>
	<hr />
</Section>

<Section backgroundColor="white" id="Construction">
	<h2>Storm Windows vs Replacement Windows: Construction</h2>
	<p>
		Both replacement windows and storm windows are available in a variety of
		construction types. This is a huge benefit for both, but it also mean
		there’s an overwhelming amount of information to wade through.
	</p>

	<p>
		Generally, replacement windows will be made of a glass that is either
		double- or triple-pane for insulation. Air or gas is injected between the
		panes for even more energy efficiency. The pane(s) are surrounded by a frame
		of aluminum, fiberglass, vinyl, or clad—interior of wood, exterior of one of
		the former.
	</p>

	<p>
		Storm windows, both interior and exterior, are made of a glass or acrylic
		panel with a wood or metal frame. Storm windows are placed over your
		existing windows and attached to the window frame using screws, caulk, a
		track system, or a compression system. Some you will be able to open your
		window easily, some you need to remove the storm window to do so.
	</p>
	<Image
		src={img2}
		alt="Shot of windows and ladder: Learn how easy it is to replace and install storm windows."
		class="max-w-96"
	/>
</Section>

<Section backgroundColor="white" id="Performance">
	<h2>Storm Windows vs Regular Windows: Performance</h2>
	<p>
		Any new window will perform better than an old window alone, especially if
		you are coming from a single pane to a double or triple. The performance of
		your replacement window is going to depend on the kind you get. For
		specifics and a breakdown of window types, visit our blog <a
			href="/resources/blog/performance-storm-windows-regular-windows"
			>Performance of Storm Windows vs Regular Window</a
		>.
	</p>
	<p>
		Adding storm windows to existing windows can make them perform almost like
		new. If you have particularly old windows, you may need to restore them
		before adding storm windows to see efficiency gains like new windows, but
		the investment is worth it.
	</p>
	<p>
		Can storm windows block noise? Yes, storm windows can block up to 50% – 70%
		of outside noise and are much less expensive than soundproof replacement
		windows. The noise reduction of the storm window depends on its ability to
		air seal and the pane material and thickness.
	</p>
	<p>
		There are many factors involved in choosing a window and a storm window, so
		using information from <a
			href="https://www.energyguide.com/info/window2.asp">Energy Guide</a
		>,
		<a href="/static/pdfs/PNNL-22855.pdf" target="_blank"
			>Department of Energy</a
		>, and PSU’s Green Lab, we’ve come up with some basics:
	</p>
</Section>

<Section backgroundColor="blue">
	<h2>Performance By Category</h2>
	<Image class="max-w-24" src={img3} />
	<h3 class="text-xl">Thermal</h3>
	<p>
		(Lower the U-Value, better the insulation) Storm windows such as Indow
		inserts have U-Value of .54. Replacement double-pane with ½” air space have
		U-Value of .48. Replacement triple-pane glass, low-E on 2 panes, ½” paces
		with argon have U-Value of .23.
	</p>

	<Image class="max-w-24" src={img4} />
	<h3 class="text-xl">Acoustic</h3>
	<p>
		Interior storm window inserts can reduce outside noise by 70% (18.9 dBA)
		when added to single-pane and 50% (10-12 dBA) when added to double-pane
		windows. Replacement windows standardly reduce outside noise by
		approximately 25%. There are replacements optimized for sound control but
		they will be a speciality window higher in price.
	</p>

	<Image class="max-w-24" src={img5} />
	<h3 class="text-xl">UV Blocking</h3>
	<p>
		Interior storm window inserts block 98% of damaging UV rays. Single-pane
		windows let in 90% of UV radiation. Standard double-pane windows let in over
		80%.
	</p>

	<Button href="/how-to/get-indow-inserts">GET A FREE ESTIMATE</Button>
</Section>

<Section backgroundColor="white" id="Installation">
	<Image
		src={img6}
		alt="Man installing replacement storm window"
		class="max-w-96"
	/>
	<h2>Window Inserts vs Full Replacement: Installation & Aesthetic</h2>
	<p>
		Replacement windows, once installed, will have a fresh, clean look. There’s
		no need for restoration or continued maintenance if you go with vinyl
		windows over wood. A variety of window shapes are available in replacement
		windows.
	</p>
	<p>
		Original windows are part of a building’s original design. Replacement
		windows can seem out of place compared to the rest of the home’s aesthetic,
		even when a custom shape is applied. If you have an older home, board
		approval is often needed before removing any old windows. Replication
		windows can be made, but those are often costly and still may not meet
		historic board approval.
	</p>
	<p>
		If you are placing storm windows in your home, you can maintain the original
		design intention. You can also choose just one or two problem windows to
		upgrade. With replacement windows, you would want to upgrade all windows at
		once to maintain a consistent look.
	</p>

	<h3>Video: How Do Storm Windows Work?</h3>
	<Youtube posterImageSrc={thumb_eoQ3UL96zwE} videoId="eoQ3UL96zwE" />
</Section>

<Section backgroundColor="grey-light">
	<h2>Testimonials</h2>
	<Image
		src={img7}
		alt="scott sidler installing hardware on energy efficient windows"
	/>
	<p class="text-xl text-indow-blue! italic">
		“24.5 years. That is the average amount of time it will take you to recover
		the financial investment of a new window. And since the typical life span of
		a replacement window is only 10-20 years, that makes replacement windows a
		very bad investment.”
	</p>
	<p>– Scott Sidler, The Craftsman Blog</p>
	<p class="text-xl text-indow-blue! italic">
		<a href="/resources/case-studies">Read more case studies.</a>
	</p>
</Section>

<Section backgroundColor="white" id="Cost">
	<h2>Cost of Storm Windows vs Replacement Windows</h2>
	<p>
		Interior storm window inserts cost on average 75% less than wood and 50%
		less than vinyl. That’s fully installed and compared to a full replacement,
		not partial. According to <a
			href="https://www.remodeling.hw.net/cost-vs-value/2019"
			rel="external">Remodeling</a
		>, 2020 cost for:
	</p>
	<p>Vinyl window replacement is $17,008</p>
	<p>Wood window replacement is $21,495</p>
	<p>The equivalent job for Indow window inserts would cost $4,050.</p>
	<p>
		Vinyl replacement windows are the least expensive option, but they don’t
		last as long. And if one part breaks, the whole window has to be replaced
		again. Energy Star windows, which are on the higher end, are not as energy
		efficient as interior storm windows, so will not save as much money on
		bills.
	</p>
	<Image
		src={img8}
		alt="woman installing interior storm window vs replacement window"
		class="max-w-64"
	/>
</Section>

<GetAFreeEstimate title="Let Us Help You" />
